import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Resizer from 'react-image-file-resizer';
import {
  passportScannerRequest,
  passportScannerCheckRequest,
} from 'helpers/api-requests/admin';

export const getFileExtension = filename => {
  if (!filename) return '';
  return filename.split('.').pop();
};

export const converBase64toBlob = (content, contentType) => {
  if (!content) {
    throw new Error('No content provided');
  }

  contentType = contentType || '';
  const sliceSize = 512;
  const byteCharacters = window.atob(content);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
};

export const usePassportScanner = ({ onScanComplete }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const clearError = () => {
    setError(null);
  };

  const clearSuccess = () => {
    setSuccess(false);
  };

  const resizeFile = file =>
    new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error('No file provided'));
        return;
      }

      Resizer.imageFileResizer(
        file,
        600,
        400,
        'JPEG',
        90,
        0,
        uri => resolve(uri),
        'base64',
        error => reject(error),
      );
    });

  const dataURIToBlob = dataURI => {
    if (!dataURI) {
      throw new Error('No data URI provided');
    }

    const splitDataURI = dataURI.split(',');
    if (splitDataURI.length < 2) {
      throw new Error('Invalid data URI format');
    }

    const byteString =
      splitDataURI[0].indexOf('base64') >= 0
        ? atob(splitDataURI[1])
        : decodeURI(splitDataURI[1]);
    const mimeString = splitDataURI[0].split(':')[1].split(';')[0];

    const ia = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ia], { type: mimeString });
  };

  const scanPassport = async file => {
    // Clear previous states
    clearError();
    setSuccess(false);

    if (!file) {
      const errorMsg = 'No file selected';
      setError(errorMsg);
      return;
    }

    setIsLoading(true);

    try {
      // Step 1: Resize the image
      const resizedImage = await resizeFile(file);

      if (!resizedImage) {
        throw new Error('Failed to resize image');
      }

      // Step 2: Convert to Blob and create FormData
      const blob = dataURIToBlob(resizedImage);
      const formData = new FormData();
      formData.append('file', blob, 'upload.jpg');

      // Step 3: Send to passport scanner API
      const response = await passportScannerRequest(formData);

      if (!response?.success) {
        const errorMsg =
          response?.message || t('passport.scanServiceNotAvailable');
        setError(errorMsg);
        setIsLoading(false);
        return;
      }

      // Step 4: Get task ID
      const taskId = response.data?.task;
      if (!taskId) {
        const errorMsg = 'Failed to start scan';
        setError(errorMsg);
        setIsLoading(false);
        return;
      }

      // Step 5: Poll for results with faster timeout
      let intervalId;
      let attempts = 0;
      const maxAttempts = 15; // Reduced from 30 to 15 (15 seconds max)
      const pollInterval = 1000; // Keep at 1 second

      const checkStatus = async () => {
        attempts++;

        try {
          const statusResponse = await passportScannerCheckRequest(taskId);

          // Check if API call failed
          if (!statusResponse?.success) {
            clearInterval(intervalId);
            setIsLoading(false);
            const errorMsg = statusResponse?.message || 'Scan check failed';
            setError(errorMsg);
            setSuccess(false);
            return;
          }

          // Check if scan is completed
          if (statusResponse.data?.completed) {
            clearInterval(intervalId);
            setIsLoading(false);

            // Check if scan had an error (data.success === false)
            if (statusResponse.data?.success === false) {
              const errorMsg =
                statusResponse.data?.error ||
                statusResponse.data?.result ||
                'Failed to scan passport. Please try again.';
              console.error('Scan error:', errorMsg);
              setError(errorMsg);
              setSuccess(false);
              return;
            }

            // Scan completed successfully! (data.success === true)
            if (
              statusResponse.data?.success === true &&
              statusResponse.data?.result
            ) {
              clearError();
              setSuccess(true); // Show success message

              // Call the callback with the extracted data
              if (onScanComplete) {
                onScanComplete(statusResponse.data.result);
              }
              return;
            }

            // If we get here, something unexpected happened
            const errorMsg = 'Unexpected response from server';
            setError(errorMsg);
            setSuccess(false);
            return;
          }

          // Check if scan timed out
          if (attempts >= maxAttempts) {
            clearInterval(intervalId);
            setIsLoading(false);
            const errorMsg = 'Scan timed out. Please try again.';
            setError(errorMsg);
            setSuccess(false);
          }
        } catch (error) {
          console.error('Status check error:', error);
          clearInterval(intervalId);
          setIsLoading(false);
          const errorMsg = error.message || 'Error checking scan status';
          setError(errorMsg);
          setSuccess(false);
        }
      };

      // Start polling
      intervalId = setInterval(checkStatus, pollInterval);
      checkStatus();
    } catch (error) {
      console.error('Scan error:', error);
      setIsLoading(false);
      const errorMsg = error.message || t('passport.scanServiceNotAvailable');
      setError(errorMsg);
      setSuccess(false);
    }
  };

  return {
    isLoading,
    handleScanPassport: scanPassport,
    scanError: error,
    clearScanError: clearError,
    scanSuccess: success,
    clearScanSuccess: clearSuccess,
  };
};
